/**
 * blog-image-single.ts — event payloads for the EDITOR-DRIVEN blog image flow
 * (idea 2026-07-19-blog-article-images-like-maira, AC v2 redo 2026-07-27).
 *
 * AC v2 rejected the v1 UX: images were produced by one opaque batch button
 * that silently picked its own prompts and quality. The maira pattern Oleg
 * asked to mirror (`maira/components/AiImageModal.tsx`) is a TWO-STEP,
 * user-visible flow driven from the editor TOOLBAR:
 *
 *   Stage 1 «expand» — the author types a rough idea in ANY language; a model
 *     (opus, per `resolveBlogModel('image_prompt_generation')`) expands it
 *     into one detailed ENGLISH image prompt, which is shown back to the
 *     author in an EDITABLE textarea. The edit gate doubles as the explicit
 *     cost confirmation before anything is generated.
 *   Stage 2 «generate» — the (possibly edited) prompt plus an EXPLICIT
 *     low/high quality choice produce ONE image, uploaded server-side; the
 *     admin receives only a public URL and inserts it AT THE CURSOR.
 *
 * Two event families, both synchronous single-handler round-trips like
 * `blog.images.*` (no webhook, no dispatched intermediate):
 *
 *   blog.image_prompt.requested  → blog.image_prompt.completed | .failed
 *   blog.image.requested         → blog.image.completed        | .failed
 *
 * Deliberately distinct from `blog.images.*` (plural): that family
 * ILLUSTRATES A WHOLE ARTICLE and writes `blog_posts_i18n.body_html` itself.
 * This family generates ONE image and writes NOTHING — the editor owns
 * placement, because the cursor position only exists client-side. A
 * cursor-inserted image therefore carries no `ai-gen` class, so a later batch
 * regenerate (which strips `ai-gen` nodes) can never destroy the author's
 * hand-placed images.
 */

import { z } from 'zod';

export const BLOG_IMAGE_SINGLE_EVENTS = {
  PROMPT_REQUESTED: 'blog.image_prompt.requested',
  PROMPT_COMPLETED: 'blog.image_prompt.completed',
  PROMPT_FAILED: 'blog.image_prompt.failed',
  IMAGE_REQUESTED: 'blog.image.requested',
  IMAGE_COMPLETED: 'blog.image.completed',
  IMAGE_FAILED: 'blog.image.failed',
} as const;

/**
 * Explicit generation quality — AC v2 item 3: no silent defaults anywhere in
 * the chain. The author picks this in the modal and it travels verbatim to
 * the gateway's `meta.quality`.
 */
export const blogImageQualitySchema = z.enum(['low', 'high']);
export type BlogImageQuality = z.infer<typeof blogImageQualitySchema>;

// ---------------------------------------------------------------------------
// blog.image_prompt.* — stage 1 (expand + translate to EN)
// ---------------------------------------------------------------------------

export const blogImagePromptRequestedPayloadSchema = z.object({
  /** Reactor correlation_id, echoed back so admin Realtime can match the run. */
  correlation_id: z.string().min(1),
  /** The author's rough idea, ANY language (pl/ru/en/…). Expanded + translated to EN. */
  user_request: z.string().min(1).max(2_000),
  /** Locale of the article being edited — the returned `alt` is written in it. */
  lang_code: z.string().min(2).max(10),
  /** Article title, when known: anchors the prompt to the article's subject (AC v2 item 5). */
  article_title: z.string().max(500).optional(),
  /**
   * Optional extra subject context (e.g. the surrounding section text,
   * tag-stripped and truncated by the caller) so the scene matches what the
   * paragraph is actually about rather than a generic interior.
   */
  article_context: z.string().max(4_000).optional(),
});
export type BlogImagePromptRequestedPayload = z.infer<
  typeof blogImagePromptRequestedPayloadSchema
>;

export const blogImagePromptCompletedPayloadSchema = z.object({
  correlation_id: z.string().min(1),
  /** The detailed ENGLISH image-generation prompt, shown editable to the author. */
  prompt: z.string().min(1),
  /** Suggested alt text in `lang_code`; the author may overwrite it. */
  alt: z.string(),
});
export type BlogImagePromptCompletedPayload = z.infer<
  typeof blogImagePromptCompletedPayloadSchema
>;

export const blogImagePromptFailedPayloadSchema = z.object({
  correlation_id: z.string().min(1),
  error: z.string(),
});
export type BlogImagePromptFailedPayload = z.infer<typeof blogImagePromptFailedPayloadSchema>;

// ---------------------------------------------------------------------------
// blog.image.* — stage 2 (generate ONE image, no body_html write)
// ---------------------------------------------------------------------------

export const blogImageRequestedPayloadSchema = z.object({
  correlation_id: z.string().min(1),
  /** The final prompt — post-author-edit. Sent to the image model verbatim. */
  prompt: z.string().min(1).max(8_000),
  /** Explicit author choice, never defaulted server-side (AC v2 item 3). */
  quality: blogImageQualitySchema,
});
export type BlogImageRequestedPayload = z.infer<typeof blogImageRequestedPayloadSchema>;

export const blogImageCompletedPayloadSchema = z.object({
  correlation_id: z.string().min(1),
  /** Public URL of the uploaded WebP in the `wandero` bucket (`blog/<uuid>.webp`). */
  url: z.string().min(1),
  cost_usd: z.number().nonnegative(),
  /** Same semantics as `blog.images.completed.cost_source`. */
  cost_source: z.enum(['billed', 'estimate', 'none']).optional(),
});
export type BlogImageCompletedPayload = z.infer<typeof blogImageCompletedPayloadSchema>;

export const blogImageFailedPayloadSchema = z.object({
  correlation_id: z.string().min(1),
  error: z.string(),
});
export type BlogImageFailedPayload = z.infer<typeof blogImageFailedPayloadSchema>;
